/**
 * app-header - Application header with navigation and user menu
 * Shows "Log In" when unauthenticated, user avatar/dropdown when authenticated
 * Navigation links are conditionally rendered based on auth state and role
 */
import { BaseElement } from '../base-element';
import { authState, canAccessSecretaryFeatures } from '../../state/auth.state';

export class AppHeader extends BaseElement {
  private dropdownOpen = false;
  private mobileMenuOpen = false;

  connectedCallback(): void {
    this.updateRender();

    // Watch auth state changes
    this.watch(authState, () => this.updateRender());

    // Close dropdown on outside click
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick(e: Event): void {
    const path = e.composedPath();
    if (!path.includes(this)) {
      if (this.dropdownOpen) {
        this.dropdownOpen = false;
        this.updateRender();
      }
      if (this.mobileMenuOpen) {
        this.mobileMenuOpen = false;
        this.updateRender();
      }
    }
  }

  public toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    this.updateRender();
  }

  public toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.updateRender();
  }

  public closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.updateRender();
  }

  public handleLogin(): void {
    this.emit('login-click');
  }

  public handleLogout(): void {
    this.emit('logout-click');
    this.dropdownOpen = false;
    this.mobileMenuOpen = false;
    this.updateRender();
  }

  public navigate(path: string): void {
    this.emit('navigate', { path });
    this.closeMobileMenu();
  }

  private getInitials(email: string | null): string {
    if (!email) return '?';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  }

  private attachEventListeners(): void {
    this.$$('[data-nav]').forEach((el) => {
      const path = el.getAttribute('data-nav');
      if (!path) return;
      el.addEventListener('click', () => this.navigate(path));
    });

    this.$$('[data-action]').forEach((el) => {
      const action = el.getAttribute('data-action');
      if (!action) return;
      el.addEventListener('click', () => {
        switch (action) {
          case 'toggle-dropdown':
            this.toggleDropdown();
            break;
          case 'toggle-mobile-menu':
            this.toggleMobileMenu();
            break;
          case 'close-mobile-menu':
            this.closeMobileMenu();
            break;
          case 'login':
            this.handleLogin();
            break;
          case 'logout':
            this.handleLogout();
            break;
          default:
            break;
        }
      });
    });
  }

  private updateRender(): void {
    const state = authState.get();
    const { isAuthenticated, email, role } = state;
    const canAccessSecretary = canAccessSecretaryFeatures.get();
    const initials = this.getInitials(email);
    const displayRole = role || 'User';

    const dropdownClasses = [
      'absolute right-4 top-14 w-56 rounded-xl border border-neutral-200 bg-white p-2 shadow-lg transition-all duration-150',
      'dark:border-white/10 dark:bg-surface-dark',
      this.dropdownOpen ? 'open opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2',
    ].join(' ');

    const overlayClasses = [
      'fixed inset-0 z-40 bg-black/60 transition-opacity duration-300',
      this.mobileMenuOpen ? 'open opacity-100 visible' : 'opacity-0 invisible',
    ].join(' ');

    const drawerClasses = [
      'fixed top-0 right-0 z-50 flex h-full w-4/5 max-w-[300px] flex-col bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.15)]',
      'transform transition-transform duration-300 dark:bg-surface-dark',
      this.mobileMenuOpen ? 'open translate-x-0' : 'translate-x-full',
    ].join(' ');

    this.render(`
      <header class="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white/95 backdrop-blur-sm dark:border-white/10 dark:bg-surface-dark/95">
        <div class="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <!-- Logo -->
          <a href="/" class="flex items-center gap-2">
            <img src="/brand/logo-primary-light.svg" alt="Venom Dog Sports" class="h-8 dark:hidden" />
            <img src="/brand/logo-primary-dark.svg" alt="Venom Dog Sports" class="hidden h-8 dark:block" />
          </a>

          <!-- Desktop Navigation -->
          <nav class="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
            <button class="inline-flex h-10 items-center rounded-lg px-4 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white" data-nav="/">
              Trials
            </button>
            ${isAuthenticated ? `
            <button class="inline-flex h-10 items-center rounded-lg px-4 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white" data-nav="/my-entries">
              My Entries
            </button>
            ` : ''}
            ${canAccessSecretary ? `
            <button class="inline-flex h-10 items-center rounded-lg px-4 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white" data-nav="/secretary">
              Secretary Portal
            </button>
            ` : ''}
          </nav>

          <!-- User Area -->
          <div class="flex items-center gap-3">
            ${!isAuthenticated ? `
            <button class="inline-flex h-10 items-center justify-center rounded-full bg-primary-500 px-6 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-primary-600" data-testid="login-btn" data-action="login">
              Log In
            </button>
            ` : `
            <button class="flex items-center gap-2 rounded-full bg-transparent py-1 pl-3 pr-1 transition-colors duration-150 hover:bg-neutral-100 dark:hover:bg-white/10" data-testid="avatar-btn" data-action="toggle-dropdown">
              <div class="hidden flex-col items-end sm:flex">
                <span class="text-sm font-semibold text-neutral-900 dark:text-white">${email?.split('@')[0] || 'User'}</span>
                <span class="text-xs text-neutral-500 dark:text-neutral-400">${displayRole}</span>
              </div>
              <div class="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white ring-2 ring-white dark:ring-surface-dark" data-testid="user-avatar">${initials}</div>
              <svg class="hidden text-neutral-400 transition-transform sm:block ${this.dropdownOpen ? 'rotate-180' : ''}" viewBox="0 0 20 20" width="20" height="20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            `}

            <!-- Mobile Menu Button -->
            <button class="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-900 transition-colors duration-150 hover:bg-neutral-100 md:hidden dark:text-white dark:hover:bg-white/10" data-testid="hamburger-btn" data-action="toggle-mobile-menu">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>

          <!-- User Dropdown (Desktop) -->
          ${isAuthenticated ? `
          <div class="${dropdownClasses}" data-testid="user-dropdown">
            <div class="mb-2 border-b border-neutral-200 px-3 pb-2 dark:border-white/10">
              <div class="text-sm font-semibold text-neutral-900 dark:text-white">${email?.split('@')[0] || 'User'}</div>
              <div class="text-xs text-neutral-500 dark:text-neutral-400">${email}</div>
            </div>
            <button class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-700 transition-colors duration-150 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-white/10" data-nav="/profile">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
              </svg>
              My Profile
            </button>
            <button class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-error-500 transition-colors duration-150 hover:bg-error-50 dark:hover:bg-error-500/10" data-testid="logout-btn" data-action="logout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
              Log Out
            </button>
          </div>
          ` : ''}
        </div>
      </header>

      <!-- Mobile Overlay -->
      <div class="${overlayClasses}" data-action="close-mobile-menu"></div>

      <!-- Mobile Navigation Drawer -->
      <div class="${drawerClasses}" data-testid="mobile-drawer">
        <div class="flex items-center justify-between px-6 pb-2 pt-6">
          <img src="/brand/logo-icon.svg" alt="" class="h-10 w-10" />
          <button class="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-white" data-action="close-mobile-menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto px-4 py-4">
          <p class="px-4 text-[11px] font-bold uppercase tracking-wide text-neutral-400">Menu</p>
          <ul class="mt-4 flex flex-col gap-2">
            <li>
              <button class="flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-semibold text-neutral-900 transition-colors duration-150 hover:bg-neutral-100 dark:text-white dark:hover:bg-white/10" data-nav="/">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="8" r="4"/><path d="M1.5 20.5s2.5-4 10.5-4 10.5 4 10.5 4"/>
                </svg>
                Trials
              </button>
            </li>
            ${isAuthenticated ? `
            <li>
              <button class="flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-semibold text-neutral-900 transition-colors duration-150 hover:bg-neutral-100 dark:text-white dark:hover:bg-white/10" data-nav="/my-entries">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                My Entries
              </button>
            </li>
            ` : ''}
            ${canAccessSecretary ? `
            <li>
              <button class="flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-base font-semibold text-neutral-900 transition-colors duration-150 hover:bg-neutral-100 dark:text-white dark:hover:bg-white/10" data-nav="/secretary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><path d="M8 21h8M12 17v4"/>
                </svg>
                Secretary Portal
              </button>
            </li>
            ` : ''}
          </ul>
        </nav>

        <div class="border-t border-neutral-200 bg-neutral-50 p-4 dark:border-white/10 dark:bg-surface-dark/70">
          ${isAuthenticated ? `
          <div class="mb-4 flex items-center gap-3 px-2">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">${initials}</div>
            <div class="flex flex-col">
              <span class="text-sm font-semibold text-neutral-900 dark:text-white">${email?.split('@')[0] || 'User'}</span>
              <span class="text-xs text-neutral-500 dark:text-neutral-400">${displayRole}</span>
            </div>
          </div>
          <button class="flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-900 transition-colors duration-150 hover:bg-neutral-100 dark:border-white/10 dark:bg-surface-dark dark:text-white dark:hover:bg-white/10" data-action="logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
            Log Out
          </button>
          ` : `
          <button class="flex w-full items-center justify-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-600" data-action="login">
            Log In
          </button>
          `}
        </div>
      </div>
    `);

    this.attachEventListeners();
  }
}

customElements.define('app-header', AppHeader);
